import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineItemType } from '../../config';
import './invoice-line-toolbar.scss';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  type: string;
}

interface InvoiceLineToolbarProps {
  visible: boolean;
  position: { top: number; left: number };
  currentType: LineItemType;
  currentTeamMemberId: string | null;
  currentIndentLevel: number;
  teamMembers: TeamMember[];
  getChargeRate: (memberId: string) => number | null;
  onChangeType: (type: LineItemType) => void;
  onChangeTeamMember: (memberId: string, memberName: string, rate: number | null) => void;
  onIndent: (level: number) => void;
  /** Effective blank-line spacing for this line, defaults resolved. */
  currentSpacing: { above: boolean; below: boolean };
  onToggleSpacing: (side: 'above' | 'below') => void;
  onInsertAbove: () => void;
  onInsertBelow: () => void;
  onDelete: () => void;
}

const TYPE_OPTIONS: { type: LineItemType; label: string; icon: string }[] = [
  { type: 'item', label: 'Fee Item', icon: '$' },
  { type: 'disbursement', label: 'Disbursement', icon: 'D' },
  { type: 'deduction', label: 'Deduction', icon: '–' },
  { type: 'header', label: 'Header', icon: 'H' },
  { type: 'text', label: 'Text', icon: 'T' },
  { type: 'bullet', label: 'Bullet', icon: '-' },
  { type: 'drawing', label: 'Drawing', icon: '#' },
  { type: 'spacer', label: 'Spacer', icon: '↕' },
];

const TYPE_LABELS: Record<string, string> = {
  item: 'Fee Item',
  disbursement: 'Disbursement',
  deduction: 'Deduction',
  header: 'Header',
  text: 'Text',
  bullet: 'Bullet',
  drawing: 'Drawing',
  spacer: 'Spacer',
};

const InvoiceLineToolbar: React.FC<InvoiceLineToolbarProps> = ({
  visible,
  position,
  currentType,
  currentTeamMemberId,
  currentIndentLevel,
  teamMembers,
  getChargeRate,
  onChangeType,
  onChangeTeamMember,
  onIndent,
  currentSpacing,
  onToggleSpacing,
  onInsertAbove,
  onInsertBelow,
  onDelete,
}) => {
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [showMemberMenu, setShowMemberMenu] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const memberMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) {
      setShowTypeMenu(false);
      setShowMemberMenu(false);
    }
  }, [visible]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node) &&
          !(e.target as Element).closest('.line-toolbar__type-btn')) {
        setShowTypeMenu(false);
      }
      if (memberMenuRef.current && !memberMenuRef.current.contains(e.target as Node) &&
          !(e.target as Element).closest('.line-toolbar__member-btn')) {
        setShowMemberMenu(false);
      }
    };
    if (showTypeMenu || showMemberMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypeMenu, showMemberMenu]);

  const handleTypeChange = useCallback((type: LineItemType) => {
    onChangeType(type);
    // Keep the menu open so the user can change again or close via click-outside
  }, [onChangeType]);

  const handleMemberChange = useCallback((member: TeamMember) => {
    const rate = getChargeRate(member.id);
    onChangeTeamMember(member.id, `${member.first_name} ${member.last_name}`, rate);
    setShowMemberMenu(false);
  }, [onChangeTeamMember, getChargeRate]);

  const handleIndentDecrease = useCallback(() => {
    if (currentIndentLevel > 0) onIndent(currentIndentLevel - 1);
  }, [currentIndentLevel, onIndent]);

  const handleIndentIncrease = useCallback(() => {
    if (currentIndentLevel < 3) onIndent(currentIndentLevel + 1);
  }, [currentIndentLevel, onIndent]);

  if (!visible) return null;

  const isSpacer = currentType === 'spacer';
  const showTeamMemberBtn = currentType === 'item' || currentType === 'disbursement';
  const currentMember = currentTeamMemberId
    ? teamMembers.find(m => m.id === currentTeamMemberId)
    : null;

  return (
    <div
      ref={toolbarRef}
      className="invoice-line-toolbar"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Type selector */}
      <div className="line-toolbar__section">
        <button
          className="line-toolbar__type-btn"
          onClick={() => { setShowTypeMenu(!showTypeMenu); setShowMemberMenu(false); }}
        >
          <span>{TYPE_LABELS[currentType] || currentType}</span>
          <span className="line-toolbar__arrow">▾</span>
        </button>

        {showTypeMenu && (
          <div ref={typeMenuRef} className="line-toolbar__menu">
            <div className="line-toolbar__menu-header">Turn into</div>
            {TYPE_OPTIONS.filter(o => o.type !== currentType).map(option => (
              <button
                key={option.type}
                className="line-toolbar__menu-item"
                onClick={() => handleTypeChange(option.type)}
              >
                <span className="line-toolbar__menu-icon">{option.icon}</span>
                <span>{option.label}</span>
              </button>
            ))}
            <div className="line-toolbar__menu-divider" />
            <button className="line-toolbar__menu-item line-toolbar__menu-item--danger" onClick={onDelete}>
              <span className="line-toolbar__menu-icon">×</span>
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>

      {/* Indent controls */}
      {!isSpacer && (
        <>
          <div className="line-toolbar__divider" />
          <div className="line-toolbar__section">
            <button
              className={`line-toolbar__indent-btn ${currentIndentLevel === 0 ? 'disabled' : ''}`}
              onClick={handleIndentDecrease}
              title="Decrease indent"
              disabled={currentIndentLevel === 0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="21" y1="6" x2="3" y2="6" />
                <line x1="21" y1="12" x2="11" y2="12" />
                <line x1="21" y1="18" x2="3" y2="18" />
                <polyline points="7 8 3 12 7 16" />
              </svg>
            </button>
            <button
              className={`line-toolbar__indent-btn ${currentIndentLevel >= 3 ? 'disabled' : ''}`}
              onClick={handleIndentIncrease}
              title="Increase indent"
              disabled={currentIndentLevel >= 3}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="13" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
                <polyline points="17 8 21 12 17 16" />
              </svg>
            </button>
          </div>
        </>
      )}

      {/* Insert a line either side of this one. These used to float in a
          separate control at the left of the row, where they read as spacing
          buttons; they belong next to the type changer. */}
      <div className="line-toolbar__divider" />
      <div className="line-toolbar__section">
        <button
          className="line-toolbar__indent-btn"
          onClick={onInsertAbove}
          onMouseDown={(e) => e.preventDefault()}
          title="Add line above"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="5" x2="21" y2="5" />
            <line x1="12" y1="11" x2="12" y2="19" />
            <line x1="8" y1="15" x2="16" y2="15" />
          </svg>
        </button>
        <button
          className="line-toolbar__indent-btn"
          onClick={onInsertBelow}
          onMouseDown={(e) => e.preventDefault()}
          title="Add line below"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="13" />
            <line x1="8" y1="9" x2="16" y2="9" />
            <line x1="3" y1="19" x2="21" y2="19" />
          </svg>
        </button>
      </div>

      {/* Blank-line spacing: one line above / below this line. A gap needs
          both neighbours to agree, so either toggle closes it. */}
      <div className="line-toolbar__divider" />
      <div className="line-toolbar__section">
        <button
          className={`line-toolbar__spacing-btn ${currentSpacing.above ? 'active' : ''}`}
          onClick={() => onToggleSpacing('above')}
          title={currentSpacing.above ? 'Space above: on (click to remove)' : 'Space above: off (click to add)'}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="2.5" x2="14" y2="2.5" />
            <path d="M8 12.5V6M5.5 8.5 8 6l2.5 2.5" />
          </svg>
        </button>
        <button
          className={`line-toolbar__spacing-btn ${currentSpacing.below ? 'active' : ''}`}
          onClick={() => onToggleSpacing('below')}
          title={currentSpacing.below ? 'Space below: on (click to remove)' : 'Space below: off (click to add)'}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="13.5" x2="14" y2="13.5" />
            <path d="M8 3.5V10M5.5 7.5 8 10l2.5-2.5" />
          </svg>
        </button>
      </div>

      {/* Team member selector */}
      {showTeamMemberBtn && (
        <>
          <div className="line-toolbar__divider" />
          <div className="line-toolbar__section">
            <button
              className="line-toolbar__member-btn"
              onClick={() => { setShowMemberMenu(!showMemberMenu); setShowTypeMenu(false); }}
            >
              <span>{currentMember ? `${currentMember.first_name} ${currentMember.last_name}` : 'Assign person'}</span>
              <span className="line-toolbar__arrow">▾</span>
            </button>

            {showMemberMenu && (
              <div ref={memberMenuRef} className="line-toolbar__menu">
                <div className="line-toolbar__menu-header">Team member</div>
                {currentTeamMemberId && (
                  <button
                    className="line-toolbar__menu-item line-toolbar__menu-item--muted"
                    onClick={() => onChangeTeamMember('', '', null)}
                  >
                    <span className="line-toolbar__menu-icon">×</span>
                    <span>Remove assignment</span>
                  </button>
                )}
                {teamMembers.map(member => {
                  const rate = getChargeRate(member.id);
                  const isActive = member.id === currentTeamMemberId;
                  return (
                    <button
                      key={member.id}
                      className={`line-toolbar__menu-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleMemberChange(member)}
                    >
                      <span className="line-toolbar__menu-icon">
                        {member.first_name[0]}{member.last_name[0]}
                      </span>
                      <span className="line-toolbar__member-info">
                        <span>{member.first_name} {member.last_name}</span>
                        {rate && <span className="line-toolbar__member-rate">${rate}/hr</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceLineToolbar;
